import React, { CSSProperties, FunctionComponent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Close } from '../../components/icons';
import { Print as PrintIcon } from '../../components/icons';
import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';
import Typography from '../Typography/Typography';
import cn from '../cn';

type DialogUIProps = {
  visible: boolean,
  max_width: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false,
  title: string,
  print_icon_visible?: boolean,
  context: React.ReactNode,
  onClose: Function,
  strict: boolean,
  show_options: boolean,
  transitionDuration?: number,
  hideCloseButton?: boolean,
  onPrint?: Function,
  cancelText?: string,
  isSlide?: boolean,
  fullScreen?: boolean,
};

// MUI Dialog maxWidth → paper max-width in px (breakpoint values; xs is 444).
// Written as literal `max-w-[...]` strings (not interpolated) so Tailwind's
// content scanner can find them.
const maxWidthClass: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string> = {
  xs: 'max-w-[444px]', sm: 'max-w-[600px]', md: 'max-w-[900px]', lg: 'max-w-[1200px]', xl: 'max-w-[1536px]',
};

const FOCUSABLE = 'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

// Visible dialogs, oldest first. Every dialog listens for keys on `document`, so with stacked
// dialogs only the topmost one may react to Escape/Tab, otherwise Escape closes them all.
const visibleDialogs: object[] = [];

const DialogUI: FunctionComponent<DialogUIProps> = ({
  fullScreen = false,
  isSlide = false,
  visible,
  max_width,
  title,
  context,
  print_icon_visible = false,
  onClose,
  strict,
  show_options,
  transitionDuration = 500,
  hideCloseButton = false,
  onPrint = () => console.log('No ink?'),
  cancelText,
}) => {
  const [render, setRender] = useState(visible);
  const [entered, setEntered] = useState(false);
  const paperRef = useRef<HTMLDivElement>(null);
  const prevFocus = useRef<Element | null>(null);
  const dialogId = useRef<object>({});

  // a dialog leaves the stack as soon as it starts closing, not after its exit transition
  useEffect(() => {
    if (!visible) return;
    const id = dialogId.current;
    visibleDialogs.push(id);
    return () => {
      visibleDialogs.splice(visibleDialogs.indexOf(id), 1);
    };
  }, [visible]);

  // mount/enter + exit-then-unmount so the transition plays both ways
  useEffect(() => {
    if (visible) {
      setRender(true);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    if (render) {
      setEntered(false);
      const t = setTimeout(() => setRender(false), transitionDuration);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // scroll lock, focus capture/restore, ESC — while rendered
  useEffect(() => {
    if (!render) return;
    prevFocus.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = setTimeout(() => paperRef.current?.focus(), 0);

    const onKey = (e: KeyboardEvent) => {
      if (visibleDialogs[visibleDialogs.length - 1] !== dialogId.current) return;
      if (e.key === 'Escape') { handleCloseDialog(false, false); return; }
      if (e.key === 'Tab' && paperRef.current) {
        const nodes = paperRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
        if (nodes.length === 0) { e.preventDefault(); return; }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      clearTimeout(focusTimer);
      (prevFocus.current as HTMLElement | null)?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [render, strict]);

  const handleCloseDialog = (status: boolean, pressed_button: boolean) => {
    if (strict === true) {
      if (pressed_button === true) onClose(status);
    } else {
      onClose(status);
    }
  };

  if (!render) return null;

  const paperTransition: CSSProperties = {
    transitionDuration: `${transitionDuration}ms`,
    ...(isSlide ? { transform: entered ? 'translateY(0)' : 'translateY(100%)' } : {}),
  };

  return createPortal(
    <div
      className="ds-dialog-backdrop transition-opacity"
      style={{ opacity: entered ? 1 : 0, transitionDuration: `${transitionDuration}ms` }}
      onClick={() => handleCloseDialog(false, false)}
    >
      <div
        ref={paperRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={paperTransition}
        className={cn(
          'ds-dialog relative flex flex-col overflow-hidden outline-none transition-transform',
          fullScreen
            ? 'h-full w-full'
            : cn('max-h-[calc(100%-32px)] w-full', max_width === false ? 'max-w-none' : maxWidthClass[max_width]),
        )}
      >
        {/* title bar */}
        <div className="ds-dialog-header relative">
          {print_icon_visible && (
            <IconButton color="default" onClick={() => onPrint()} className="absolute right-9 top-1.5"><PrintIcon /></IconButton>
          )}
          {!hideCloseButton && (
            <IconButton color="default" onClick={() => handleCloseDialog(false, true)} className="absolute right-2 top-1.5"><Close /></IconButton>
          )}
          <Typography align="center" variant="h3" text={title} />
        </div>

        {/* content */}
        <div className="ds-dialog-body grow overflow-y-auto">
          {context}
        </div>

        {/* actions */}
        {show_options && (
          <div className="ds-dialog-footer">
            <Button variant="text" onClick={() => handleCloseDialog(false, true)} color="secondary">
              {cancelText !== undefined ? 'Cancel' : 'Cancel/Decline'}
            </Button>
            <Button variant="text" onClick={() => handleCloseDialog(true, true)} color="primary">
              Confirm
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.getElementById('root') ?? document.body,
  );
};

export default DialogUI;
